// @react-compiler enable
import {testIdProps} from '@github-ui/test-id-props'
import {PlusIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'

import {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../../api/columns/contracts/storage'
import type {TitleValueWithContentType} from '../../../api/columns/contracts/title'
import {IssueState, type ParentIssue} from '../../../api/common-contracts'
import {ItemType} from '../../../api/memex-items/item-type'
import {TableRowHeight} from '../../../api/view/contracts'
import {useTableCopilotTasks} from '../../../features/copilot-tasks/use-table-copilot-tasks'
import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {isNumber, parseTitleNumber} from '../../../helpers/parsing'
import {getTableRowHeightLineClamp} from '../../../helpers/table-row-height'
import {useTableSidePanel} from '../../../hooks/use-side-panel'
import {useTableRowHeight} from '../../../hooks/use-table-row-height'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import {IssueModel, type MemexItemModel} from '../../../models/memex-item-model'
import {MemexItemIcon} from '../../common/memex-item-icon'
import {TextPlaceholder} from '../../common/placeholders'
import {InteractiveItemTitle} from '../../interactive-item-title'
import {useStartIssueCreator} from '../../issue-creator'
import {BaseCell} from '../cells/base-cell'
import {ExpandButton, type ExpandButtonProps} from '../expand-button'
import {TableFocusType, useTableNavigation} from '../navigation'
import {useRecordCellRenderer} from '../performance-measurements'
import styles from './title-renderer.module.css'

type Props = Readonly<
  {
    currentValue: ColumnValue<TitleValueWithContentType>
    model: MemexItemModel
    isDisabled?: boolean
    rowId: string
    /** If defined, represents the number of levels deep this item is in a sub-issues hierarchy. */
    subIssueDepth?: number
  } & Omit<ExpandButtonProps, 'issueId'>
>

export const LoadingTitleCell = () => (
  <BaseCell>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

const AddSubIssueComponent = memo(function AddSubIssueComponent({
  rowId,
  model,
}: {
  rowId: string
  model: MemexItemModel
}) {
  const issueCreator = useStartIssueCreator()
  const {state} = useTableNavigation()
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Build the parent issue update action to pass to the issue creator
  const parentIssueUpdateAction: UpdateColumnValueAction | undefined = useMemo(() => {
    if (!(model instanceof IssueModel)) return undefined

    const nwo = model.getNameWithOwner()
    if (!nwo || nwo.length !== 2) return undefined

    const [owner, repository] = nwo
    if (!owner || !repository) return undefined

    // Global Relay ID is required in order to create a sub-issue, because it uses the GraphQL API
    const globalRelayId = model.content.globalRelayId
    if (!globalRelayId) return undefined

    // Construct a ParentIssue object from the model's data
    const parentIssue: ParentIssue = {
      id: model.content.id,
      globalRelayId,
      number: model.getItemNumber(),
      state: model.state ?? IssueState.Open,
      stateReason: model.stateReason,
      nwoReference: model.getNameWithOwnerReference(),
      title: model.getRawTitle(),
      titleHtml: model.getHtmlTitle(),
      url: model.content.url,
      repository,
      owner,
      updatedAt: model.updatedAt || new Date().toISOString(),
    }

    return {
      dataType: MemexColumnDataType.ParentIssue,
      value: parentIssue,
    }
  }, [model])

  const onAddSubIssue = useCallback(() => {
    if (!issueCreator || !parentIssueUpdateAction) return

    // Open the issue create dialog directly with the parent issue prefilled
    issueCreator.start(
      {
        // Get repository from parent issue to default to the same repo
        repo: parentIssueUpdateAction.value
          ? {
              owner: parentIssueUpdateAction.value.owner,
              name: parentIssueUpdateAction.value.repository,
            }
          : undefined,
      },
      {
        isCreatingSubIssue: true,
        updateColumnActions: [parentIssueUpdateAction],
        // Set the group ID to the parent issue as if we were grouped by parent issue
        groupId: parentIssueUpdateAction.value?.nwoReference,
      },
      buttonRef,
    )
  }, [issueCreator, parentIssueUpdateAction])

  // Use effect to focus the add sub-issue button when navigation state changes to it
  useEffect(() => {
    if (state.focus?.type === TableFocusType.ADD_SUB_ISSUE_BUTTON && state.focus.details?.parentY === rowId) {
      buttonRef.current?.focus()
    }
  }, [state.focus, rowId])

  return (
    <div className={styles.PlusIconContainer}>
      <IconButton
        ref={buttonRef}
        icon={PlusIcon}
        aria-label="Add sub-issue"
        size="small"
        onClick={e => {
          e.stopPropagation()
          onAddSubIssue()
        }}
        onKeyDown={e => {
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            onAddSubIssue()
          }
        }}
      />
    </div>
  )
})

export const TitleRenderer = memo(function TitleRenderer({
  currentValue,
  model,
  isDisabled,
  subIssueDepth = 0,
  expandable,
  expanded,
  rowId,
  expandRef,
}: Props) {
  useRecordCellRenderer('TitleRenderer', model.id)
  const titleValue = hasValue(currentValue) ? currentValue.value : undefined
  const number = parseTitleNumber(titleValue)
  const {openPane} = useTableSidePanel()
  const {hierarchyEnabled} = useTableHierarchy()
  const {copilotTasksEnabled} = useTableCopilotTasks()
  const issueId = model.contentType === 'Issue' ? model.content.id.toString() : undefined
  const {rowHeight} = useTableRowHeight()
  const isWrapping = rowHeight !== TableRowHeight.Single
  const lineClamp = getTableRowHeightLineClamp(rowHeight)

  return (
    <BaseCell className={styles.titleCell}>
      {(hierarchyEnabled || copilotTasksEnabled) && (
        <div
          style={{'--hierarchy-depth': subIssueDepth, '--expandable': expandable ? 1 : 0}}
          className={clsx(styles.hierarchyDepth, styles.expandButtonBox)}
        >
          <ExpandButton
            expandRef={expandRef}
            expanded={expanded}
            expandable={expandable}
            issueId={issueId}
            nwoReference={model.getNameWithOwnerReference()}
            rowId={rowId}
          />
        </div>
      )}
      <div className={styles.Box}>
        <MemexItemIcon titleColumn={currentValue} isBlocked={model.isBlocked()} />
      </div>
      <InteractiveItemTitle
        model={model}
        isDisabled={isDisabled}
        currentValue={currentValue}
        openPane={openPane}
        isWrapping={isWrapping}
        lineClamp={lineClamp}
      />
      {isNumber(number) && (
        <div className={styles.Box_1}>
          <span className={styles.Text}> #{number}</span>
        </div>
      )}
      {hierarchyEnabled && model.contentType === ItemType.Issue && <AddSubIssueComponent rowId={rowId} model={model} />}
    </BaseCell>
  )
})
